<template>
  <div class="home-office-select-wrapper">
    <Multiselect
      :id="inputId"
      :model-value="innerValue"
      @update:model-value="onSelect"
      :options="options"
      label="name"
      track-by="id"
      :placeholder="placeholder"
      :multiple="multiple"
      :close-on-select="!multiple"
      :searchable="true"
      class="home-office-select"
      :class="inputClass"
    >
      <template #afterList>
        <div
          v-if="hasMore"
          class="multiselect__load-more"
          @mousedown.prevent
          @click.prevent="loadMore"
        >
          <button type="button" class="load-more-btn" :disabled="loadingMore">
            {{ loadingMore ? 'Loading...' : 'Load more' }}
          </button>
        </div>
      </template>
    </Multiselect>
    <p v-if="errors && errors.length" class="input--error mt-1 text-sm text-red-600">
      {{ errors[0].message }}
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import { fetchHomeOfficesPage } from '@/services/users/'

export default {
  name: 'HomeOfficeSelect',
  components: { Multiselect },
  props: {
    modelValue: {
      type: [String, Array],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select Home Office',
    },
    inputId: {
      type: String,
      default: 'homeOffice',
    },
    inputClass: {
      type: String,
      default: '',
    },
    errors: {
      type: Array,
      default: () => [],
    },

    initialOptions: {
      type: Array,
      default: () => [],
    },

    initialHasMore: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const options = ref([])
    const nextPage = ref(1)
    const hasMore = ref(false)
    const loadingMore = ref(false)

    const innerValue = computed(() => {
      const val = props.modelValue
      if (props.multiple) {
        if (!Array.isArray(val) || !val.length) return []
        return val
          .map((v) => (typeof v === 'object' && v?.id ? v : options.value.find((o) => o.id === v)))
          .filter(Boolean)
      }
      if (val == null || val === '') return null
      return options.value.find((o) => o.id === val) || null
    })

    const onSelect = (selected) => {
      if (props.multiple) {
        emit('update:modelValue', Array.isArray(selected) ? selected : [])
      } else {
        emit('update:modelValue', selected ? selected.id : '')
      }
    }

    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return
      loadingMore.value = true
      try {
        const res = await fetchHomeOfficesPage(nextPage.value)
        const nextResults = res.results || []
        options.value = [...options.value, ...nextResults]
        nextPage.value += 1
        hasMore.value = !!res.next
      } catch (error) {
        alert(error)
      } finally {
        loadingMore.value = false
      }
    }

    const loadFirstPage = async () => {
      try {
        const res = await fetchHomeOfficesPage(1)
        options.value = res.results || []
        nextPage.value = 2
        hasMore.value = !!res.next
      } catch (error) {
        alert(error)
      }
    }

    onMounted(() => {
      if (props.initialOptions && props.initialOptions.length > 0) {
        options.value = [...props.initialOptions]
        nextPage.value = 2
        hasMore.value = props.initialHasMore
      } else {
        loadFirstPage()
      }
    })

    return {
      options,
      hasMore,
      loadingMore,
      innerValue,
      onSelect,
      loadMore,
    }
  },
}
</script>

<style scoped>
.home-office-select :deep(.multiselect__content-wrapper) {
  max-height: 200px;
  overflow-y: auto;
}
.multiselect__load-more {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 6px 8px;
}
.load-more-btn {
  width: 100%;
  padding: 6px 10px;
  font-size: 0.875rem;
  color: #4f46e5;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: center;
}
.load-more-btn:hover:not(:disabled) {
  background: #f3f4f6;
}
.load-more-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
